import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDayPage, saveDayNote, type DayLogEntry } from '../api';
import { MarkdownEditor } from './MarkdownEditor';
import { QueryState } from './QueryState';
import { PeriodNav } from './PeriodNav';
import { todayInZone, shiftDay } from '../timezones';
import { readableDay, readableInstant } from '../dateLabels';

/**
 * One day, as the app recorded it, beside what the account made of it.
 *
 * ## The columns are the other way round from the mockup, because of what the probe found
 *
 * The mockup drew the editor as the page and the log as a sidebar. Measured on the instance first:
 * **sixteen notes exist across all twenty-two accounts**, every one written by a review agent, none
 * attached to a task, the newest fifteen days old. Nobody here writes notes. So the editor is a
 * bet, and a page whose main column is a bet is a blank page — which is the thing every journal app
 * dies of, and the thing the task says this design is meant to avoid ("this page is never blank,
 * because the day has already filled half of it before the person arrives").
 *
 * The record leads, therefore, and the editor sits beneath it under a heading that invites rather
 * than demands. If the first measurable — days with words, per active account, per week — stays at
 * zero after a month, folding the editor away is a CSS change and not a redesign.
 *
 * ## The empty day is the common case, not the edge
 *
 * Same probe: across the current week and all twenty-two accounts, **three** account-days had
 * anything on them at all, and the largest had three entries. So an empty page is what most days
 * look like, and it says what it means — "nothing was recorded" — rather than drawing a frame
 * around nothing. The three-entry day is the design target; the 111-entry day exists (one account,
 * one day in August) and scrolls.
 */

const KIND_LABEL: Record<DayLogEntry['kind'], string> = {
  done: 'Completed',
  habit: 'Habit',
  block: 'Block',
  carried: 'Carried',
};

/**
 * The clock beside a timed entry, in the account's zone.
 *
 * Never called for an untimed one — those are rendered in their own band, and giving them a
 * fallback string here is how "no time of day" quietly becomes "00:00".
 */
const clockAt = (at: string, timezone: string): string => readableInstant(at, timezone, 'clock');

function LogLine({ entry, timezone }: { entry: DayLogEntry; timezone: string }) {
  return (
    <li className={`day-log-line kind-${entry.kind}`}>
      <span className="day-log-when">{entry.at ? clockAt(entry.at, timezone) : ''}</span>
      <span className="day-log-kind">{KIND_LABEL[entry.kind]}</span>
      <span className="day-log-what">
        {entry.title}
        {entry.detail && <span className="day-log-detail"> {entry.detail}</span>}
      </span>
    </li>
  );
}

export function DayPage({
  date,
  timezone,
  onChangeDate,
}: {
  date: string;
  timezone: string;
  onChangeDate: (day: string) => void;
}) {
  const queryClient = useQueryClient();
  const page = useQuery({ queryKey: ['day-page', date], queryFn: () => getDayPage(date) });

  /*
    The editor is a controlled field with its own draft, seeded once per day.

    Keyed on the date so moving to yesterday re-seeds it, and guarded by `seededFor` so a refetch
    of the same day — window focus, the save's own invalidation — does not overwrite what is being
    typed with what the server last stored.
  */
  const [draft, setDraft] = useState('');
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (page.data && seededFor.current !== date) {
      setDraft(page.data.note?.bodyMd ?? '');
      seededFor.current = date;
    }
  }, [page.data, date]);

  const save = useMutation({
    mutationFn: (bodyMd: string) => saveDayNote(date, bodyMd),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['day-page', date] });
      // The month grid's marks are derived from these bodies, so a first save has to reach it.
      void queryClient.invalidateQueries({ queryKey: ['month'] });
    },
  });

  const today = todayInZone(timezone);
  const timed = page.data?.log.filter((e) => e.at !== null) ?? [];
  const untimed = page.data?.log.filter((e) => e.at === null) ?? [];

  return (
    <section className="day-page">
      {/*
        `PeriodNav`, not a pair of arrows of its own. It already owns "step back, step forward, and
        whatever belongs between them" for Today and the week canvas, its arrows already say what
        they step to a screen reader, and a third spelling of the same control on a third screen is
        how two of them come to disagree about which side "next" is on.
      */}
      <header className="day-page-head">
        <PeriodNav
          label="day"
          onPrevious={() => onChangeDate(shiftDay(date, -1))}
          onNext={() => onChangeDate(shiftDay(date, 1))}
        >
          <div className="day-page-title">
            <h2>{readableDay(date, 'weekday')}</h2>
            {date !== today && (
              <button type="button" className="plain" onClick={() => onChangeDate(today)}>
                Back to today
              </button>
            )}
          </div>
        </PeriodNav>
      </header>

      <QueryState query={page} what="this day" />

      {page.data && (
        <>
          <div className="day-page-log">
            <h3>What happened</h3>
            {page.data.log.length === 0 ? (
              /*
                The common case, and it says which kind of nothing it is.

                "Nothing recorded" and "we could not read it" are different facts — `QueryState`
                above owns the second, so this one is free to be plain about the first rather than
                hedging.
              */
              <p className="muted">
                Nothing was recorded on this day — no task finished, no habit logged, no block
                placed.
              </p>
            ) : (
              <>
                <ul className="day-log">
                  {timed.map((entry, i) => (
                    <LogLine key={`t${i}`} entry={entry} timezone={timezone} />
                  ))}
                </ul>
                {untimed.length > 0 && (
                  <>
                    {/*
                      The band for things the day holds without an hour.

                      A habit entry counts towards a *date*: `occurred_on` is a date column, and on
                      the instance 92% of entries were written on some later day, so the only
                      instant on the row is when it was typed. Printing these under a heading that
                      says so is the alternative to giving them a clock nobody looked at.
                    */}
                    <p className="day-log-band muted">No time recorded</p>
                    <ul className="day-log">
                      {untimed.map((entry, i) => (
                        <LogLine key={`u${i}`} entry={entry} timezone={timezone} />
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
            <p className="muted day-page-footer">
              {/*
                The counts, printed — not a sentence stating a rule about them.

                `0624005` on this branch is the precedent and the reason: the Reflection caption
                asserted a rule that a measured probe found false on the days that mattered, and
                the fix was to print the two figures side by side so a reader can check either
                against the payload it came from. A footer here reading "everything on this day"
                would be making the same kind of claim — and it would be false for exactly the
                population this page is careful about, since a synced calendar meeting is on the
                day and deliberately not on the page. So: four numbers, each naming what it counted.
              */}
              {page.data.counts.done} completed · {page.data.counts.habits} habit entries ·{' '}
              {page.data.counts.blocks} blocks · {page.data.counts.carried} carried onto this day.
              Written by the app from what it recorded; the notes below are yours.
            </p>
          </div>

          <div className="day-page-note">
            <h3>Your note</h3>
            <MarkdownEditor
              value={draft}
              onChange={setDraft}
              onBlur={() => {
                if (draft !== (page.data?.note?.bodyMd ?? '')) save.mutate(draft);
              }}
              rows={10}
              label="What this day was like"
              placeholder="What went well, what went sideways, and why."
            />
            <p className="muted day-page-saved" aria-live="polite">
              {save.isPending
                ? 'Saving…'
                : save.isError
                  ? 'Could not save that. It is still in the box — try again.'
                  : page.data.note
                    ? `Saved ${clockAt(page.data.note.updatedAt, timezone)}`
                    : 'Not saved yet.'}
            </p>
          </div>
        </>
      )}
    </section>
  );
}
